import React from 'react';
import { User as UserIcon, ShieldCheck, Upload, Building2, Wallet as WalletIcon, Lock, BookOpen } from 'lucide-react';
import { TENANT } from '../../config';
import { isIdentityVerified } from '../../utils/onboardingFromUser';
import { ProfileSection } from './sections/ProfileSection';
import { IdentitySection } from './sections/IdentitySection';
import { AccreditationSection } from './sections/AccreditationSection';
import { BankSection } from './sections/BankSection';
import { WalletSection } from './sections/WalletSection';
import { SecuritySection } from './sections/SecuritySection';
import { DocsSection } from './sections/DocsSection';

export type SettingsGroup = 'Account' | 'Payment methods' | 'Security' | 'Documents';

export interface SettingsItem {
  /** URL segment: /portal/settings/:key — matches the ?tab= keys for legacy redirects. */
  key: string;
  label: string;
  description: string;
  group: SettingsGroup;
  icon: React.ComponentType<{ className?: string }>;
  Component: React.ComponentType;
  /** Optional: hide this section (list row + detail route) for the given user. Used for KYC once
   *  identity is verified — it's terminal, and the status already shows in Profile → Account
   *  Details and the onboarding banner, so a dedicated section is redundant. */
  isHidden?: (user: any) => boolean;
  /**
   * Only exists where the tenant sells investments. An eMerge employee opening Settings was
   * offered "Verify your identity to invest", "Verify your accredited investor status", a bank
   * account to fund purchases and a crypto wallet — four rows of another company's onboarding
   * on a hearing-care workforce account. Rows marked here are dropped from both the list and
   * the detail route when `TENANT.features.investing` is off, so typing the URL gets nothing
   * either.
   */
  investing?: true;
}

/**
 * Single source of truth for the Settings sections — drives the grouped landing list and the
 * detail route. Add, reorder, or regroup a section by editing this array.
 */
export const SETTINGS_ITEMS: readonly SettingsItem[] = [
  { key: 'profile', group: 'Account', label: 'Profile Information', description: 'Your name, contact, and account details', icon: UserIcon, Component: ProfileSection },
  { key: 'kyc', group: 'Account', label: 'Identity Verification', description: 'Verify your identity to invest', icon: ShieldCheck, Component: IdentitySection, isHidden: isIdentityVerified, investing: true },
  { key: 'accreditation', group: 'Account', label: 'Accreditation', description: 'Verify your accredited investor status', icon: Upload, Component: AccreditationSection, investing: true },
  { key: 'bank', group: 'Payment methods', label: 'Bank Accounts', description: 'Manage your linked bank accounts', icon: Building2, Component: BankSection, investing: true },
  { key: 'wallet', group: 'Payment methods', label: 'Crypto Wallet', description: 'Connect a Web3 wallet', icon: WalletIcon, Component: WalletSection, investing: true },
  { key: 'security', group: 'Security', label: 'Security', description: 'Password, two-factor, and sessions', icon: Lock, Component: SecuritySection },
  { key: 'docs', group: 'Documents', label: 'Documentation', description: 'Legal agreements and signed documents', icon: BookOpen, Component: DocsSection },
];

/** The order the groups render in on the settings list. */
export const SETTINGS_GROUPS: readonly SettingsGroup[] = ['Account', 'Payment methods', 'Security', 'Documents'];

/** Whether this tenant offers a section at all — separate from per-user `isHidden`. */
const offeredHere = (item: SettingsItem): boolean => !item.investing || TENANT.features.investing;

/** Items for a group, in registry order, limited to what this tenant offers. */
export const itemsByGroup = (group: SettingsGroup): SettingsItem[] =>
  SETTINGS_ITEMS.filter((i) => i.group === group && offeredHere(i));

export const getSettingsItem = (key?: string): SettingsItem | undefined =>
  SETTINGS_ITEMS.find((i) => i.key === key && offeredHere(i));

/**
 * Which settings sections still need action, given the completed onboarding step ids. Maps the
 * onboarding steps that get done in Settings to their section (identity → kyc, funding →
 * wallet/bank by tenant). Used for the "action needed" hint on list rows.
 *
 * Investor onboarding only: a workforce account has no identity-to-invest or funding step, so
 * nothing here can be outstanding for it.
 */
export function sectionsNeedingAttention(completedSteps: string[]): Set<string> {
  const attention = new Set<string>();
  if (!TENANT.features.investing) return attention;
  if (!completedSteps.includes('verify-identity')) attention.add('kyc');
  if (!completedSteps.includes('connect-funding')) attention.add(TENANT.features.crypto ? 'wallet' : 'bank');
  return attention;
}
